/* eslint-disable camelcase */
import React, {
  useContext, useEffect, useMemo
} from 'react'
import cookie from 'react-cookies'
import { Route, Switch, useLocation } from 'react-router-dom'
import DealerActivationPaths from '@sites/dealer/activation/DealerActivationPaths'
import { ErrorBoundary } from 'react-error-boundary'

import AccountSetupPage from '@sites/shared/AccountSetupPage'
import TransactionSummaryPage from '@sites/shared/TransactionSummaryPage'
import SimSelectionPage from '@sites/shared/SimSelectionPage'
import NewPhoneNumberPage from '@sites/shared/PhoneNumberPage'
import PlansPage from '@sites/shared/PlansPage'

// Consumer activation pages that are shared with dealer flow
import IntroPage from '@sites/consumer/activation/IntroPage'
import PlanCardFooter from '@sites/consumer/activation/PlanCardFooter'
import PaymentPage from '@sites/consumer/activation/PaymentPage'
import VerifyPage from '@sites/consumer/activation/VerifyPage'

import SiteHeader from '@apps/components/SiteHeader'

import { useDispatch, useSelector } from 'react-redux'
import { setUserType } from '@apps/redux/features/CommonSlice'
import { updateAccountSetupState } from '@apps/redux/features/AccountSetupSlice'
import { PLANS_STATE } from '@apps/redux/features/PlansSlice'
import {
  ApplicationContext,
  APPLICATION_STATE,
} from '@apps/contexts/ApplicationContext'
import Sites from '@sites/index'

import ErrorSection from '@apps/flows/activation/sections/ErrorSection'
import ProcessLoader from '@sites/dealer/activation/ProcessLoader'
import ProcessFailure from '@sites/dealer/activation/ProcessFailure'
import Loader, { SIZE } from '@apps/components/Loader'
import AlreadyActivated from '@/sites/dealer/activation/AlreadyActivated'

const ActivationMount = () => {
  const dispatch = useDispatch()

  const {
    [APPLICATION_STATE.PROVINCES]: mappedProvinceList,
    [APPLICATION_STATE.CURRENT_LANGUAGE]: currentLanguage,
    [APPLICATION_STATE.IDENTITY]: identity,
    [APPLICATION_STATE.IS_LANDING_LOGIC_NOT_COMPLETE]: is_landing_logic_not_complete,
    [APPLICATION_STATE.IS_ACTIVATE_ALL_TRIGERRED]: is_activate_all_trigerred
  } = useContext(ApplicationContext)

  const { outletId } = identity || {}

  const {
    [PLANS_STATE.PRE_SELECTED_PLAN]: preSelectedPlan,
    [PLANS_STATE.PLAN]: currentPlan
  } = useSelector(
    (state) => state.plansPage
  )

  const dealerActivationPaths = new DealerActivationPaths()
  const location = useLocation()
  const isSplashPath = location.pathname === dealerActivationPaths.Splash

  const provinces = useMemo(() => {
    return mappedProvinceList?.map((item) => {
      const { label, value, shortName } = item
      return {
        value,
        name: label[currentLanguage],
        shortName
      }
    })
  }, [mappedProvinceList])

  const provinceCookie = cookie.load('prov')
  useEffect(() => {
    if (provinces && provinceCookie) {
      const province = provinces?.find(
        (prov) => prov.shortName?.toLowerCase() === provinceCookie.toLowerCase()
      )
      if (province) {
        dispatch(updateAccountSetupState({ province: province.value }))
      }
    }
  }, [JSON.stringify(provinces), provinceCookie])

  useEffect(() => {
    const currentPath = window.location.pathname
    if (
      currentPath !== dealerActivationPaths.Base &&
      currentPath !== dealerActivationPaths.Splash
    ) {
      window.location = dealerActivationPaths.Base
    }

    dispatch(setUserType(Sites.dealer))
  }, [])

  // Wait for identity to load before deciding on session validity
  if (identity === undefined) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader size={SIZE.MD} loading />
      </div>
    )
  }

  // Session invalid if no outletId
  if (!outletId) {
    return (
      <code className="text-gray-900">
        {JSON.stringify({
          error: 711,
          message: 'Session expired'
        })}
      </code>
    )
  }

  return (
    <>
      <SiteHeader />
      <ErrorBoundary fallback={<ErrorSection />}>
        {!isSplashPath && is_activate_all_trigerred && (
          <div className='fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-100 overflow-hidden bg-white flex flex-col items-center justify-center opacity-50' />
        )}
        {!isSplashPath && is_landing_logic_not_complete && !is_activate_all_trigerred && (
          <div className={`fixed top-0 left-0 right-0 bottom-0 w-full h-screen z-100 overflow-hidden bg-white flex flex-col items-center justify-center ${is_landing_logic_not_complete === true ? 'opacity-100' : 'opacity-50'
            }`}
          >
            <Loader size={SIZE.MD} loading />
          </div>
        )}
        <Switch>
          <Route exact path={dealerActivationPaths.Base}>
            <IntroPage path={dealerActivationPaths.Base} />
          </Route>
          <Route path={dealerActivationPaths.AccountSetup}>
            <AccountSetupPage />
          </Route>
          <Route path={dealerActivationPaths.SimSelection}>
            <SimSelectionPage />
          </Route>
          <Route path={dealerActivationPaths.TransactionSummary}>
            <TransactionSummaryPage />
          </Route>
          <Route path={dealerActivationPaths.AlreadyActivated}>
            <AlreadyActivated />
          </Route>
          <Route path={dealerActivationPaths.ChooseNumber}>
            <NewPhoneNumberPage />
          </Route>
          <Route path={dealerActivationPaths.ProcessLoader}>
            <ProcessLoader />
          </Route>
          <Route path={dealerActivationPaths.ProcessFailure}>
            <ProcessFailure />
          </Route>
          <Route path={dealerActivationPaths.Plans}>
            <PlansPage />
            {(preSelectedPlan || !!currentPlan?.sluggedName) && <PlanCardFooter />}
          </Route>
          <Route path={dealerActivationPaths.Payment}>
            <PaymentPage />
          </Route>
          <Route path={dealerActivationPaths.Verify}>
            <VerifyPage />
          </Route>
          <Route path={dealerActivationPaths.Splash}>
            <ErrorSection mfeError={location.state?.error || null} />
          </Route>
          <Route path="*">
            <ErrorSection errorNum={404} />
          </Route>
        </Switch>
      </ErrorBoundary>
    </>
  )
}

export default ActivationMount
